import React, { useState } from 'react';
import { TABLE_COLUMNS, getTableColumnPrefs } from '@vanilla/constants/DealDisplayFields.js';
import { PipelineStore } from '@vanilla/constants/Pipelines.js';
import { getStageLabel } from '@vanilla/constants/Stages.js';
import { permissions } from '@vanilla/services/PermissionService.js';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '@/components/ui/dialog';

function csvEscape(val) {
    const s = String(val ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) {
        return '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
}

function formatAmount(val) {
    if (!permissions.canViewFinancials('deals')) return '';
    const num = Number(val);
    if (isNaN(num) || num === 0) return '0.00';
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

const ALL_FIELDS_HEADER = [
    'Deal Name', 'Stage', 'Amount', 'Probability',
    'Forecast Amount', 'Account', 'Sales Rep', 'Client ID',
    'Expected Close Date', 'Pipeline', 'Status',
    'Type', 'Lead Source', 'Service Line', 'Next Step', 'Campaign Source',
    'Forecast Category', 'Created Date', 'Created By', 'Last Modified Date', 'Last Modified By',
    'Contract Value', 'Payment Terms', 'Payment Model', 'Invoice Frequency',
    'Invoice Amount', 'Total Invoices', 'Expected First Invoice Date',
    'Last Invoice Date', 'Revenue Start Date', 'Revenue End Date',
    'SOW Signing Date', 'Original Created Date', 'Deal Date', 'Actual Close Date',
    'Lost Reason', 'Primary Contact',
];

function getAllFieldValues(deal) {
    return [
        deal.dealName || deal.name || '',
        getStageLabel(deal.dealStage) || deal.dealStage || '',
        formatAmount(deal.dealAmount ?? 0),
        deal.probability != null ? `${deal.probability}%` : '',
        formatAmount(deal.forecastAmount ?? 0),
        deal.accountName || deal.clientName || '',
        deal.dealOwner || '',
        deal.clientId || '',
        deal.expectedCloseDate || '',
        PipelineStore.getLabel(deal.dealPipeline) || deal.dealPipeline || '',
        deal.status || '',
        deal.dealType || '',
        deal.leadSource || '',
        deal.serviceLine || '',
        deal.nextStep || '',
        deal.campaignSource || '',
        deal.forecastCategory || '',
        deal.createdAt || '',
        deal.createdByName || deal.createdBy || '',
        deal.updatedAt || '',
        deal.updatedByName || deal.updatedBy || '',
        formatAmount(deal.contractValue ?? deal.dealAmount ?? 0),
        deal.paymentTerms || '',
        deal.paymentModel || '',
        deal.invoiceFrequency || '',
        formatAmount(deal.invoiceAmount ?? 0),
        deal.paymentCount != null ? deal.paymentCount : 0,
        deal.expectedFirstInvoiceDate || '',
        deal.lastInvoiceDate || '',
        deal.revenueStartDate || '',
        deal.revenueEndDate || '',
        deal.sowSigningDate || '',
        deal.originalCreatedDate || '',
        deal.dealDate || '',
        deal.actualCloseDate || '',
        deal.lostReason || '',
        deal.primaryContactName || '',
    ];
}

function getSelectedFieldValue(deal, key) {
    switch (key) {
        case 'dealName': return deal.dealName || deal.name || '';
        case 'clientName': return deal.accountName || deal.clientName || '';
        case 'dealOwner': return deal.dealOwner || '';
        case 'dealStage': return getStageLabel(deal.dealStage) || deal.dealStage || '';
        case 'dealAmount': return formatAmount(deal.dealAmount ?? 0);
        case 'forecastAmount': return formatAmount(deal.forecastAmount ?? 0);
        case 'probability': return deal.probability != null ? `${deal.probability}%` : '';
        case 'expectedCloseDate': return deal.expectedCloseDate || '';
        case 'nextStep': return deal.nextStep || '';
        case 'status': return deal.status || '';
        case 'dealPipeline': return PipelineStore.getLabel(deal.dealPipeline) || deal.dealPipeline || '';
        case 'createdAt': return deal.createdAt || '';
        case 'createdByName': return deal.createdByName || deal.createdBy || '';
        case 'updatedAt': return deal.updatedAt || '';
        case 'updatedByName': return deal.updatedByName || deal.updatedBy || '';
        default: return '';
    }
}

export default function ExportModal({ deals, onClose }) {
    const [scope, setScope] = useState('all');

    const buildRows = (list) => {
        const header = scope === 'all' ? ALL_FIELDS_HEADER : (() => {
            const prefKeys = getTableColumnPrefs();
            const colMap = new Map(TABLE_COLUMNS.map((c) => [c.key, c]));
            return prefKeys.map((k) => colMap.get(k)).filter(Boolean).map((c) => c.label);
        })();
        const rows = list.map((d) => {
            const values = scope === 'all'
                ? getAllFieldValues(d)
                : getTableColumnPrefs()
                    .map((k) => TABLE_COLUMNS.find((c) => c.key === k))
                    .filter(Boolean)
                    .map((c) => getSelectedFieldValue(d, c.key));
            return values.map(csvEscape);
        });
        return [header.map(csvEscape), ...rows.map((r) => r.join(','))].join('\n');
    };

    const download = (csv) => {
        const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `crm-deals-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
    };

    const handleOk = () => {
        if (!permissions.canExport('deals')) return;
        download(buildRows(deals));
        onClose();
    };

    return (
        <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
            <DialogContent
                unstyled
                hideClose
                id="exportCsvModal"
                className="modal-content modal-small"
            >
                <div className="modal-header">
                    <DialogTitle unstyled>Export Deals</DialogTitle>
                    <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>&times;</button>
                </div>
                <div className="modal-body">
                    <p className="display-settings-hint">Choose which fields to include in the CSV export.</p>
                    <div className="export-csv-options">
                        <label className="export-csv-option">
                            <input type="radio" name="exportScope" value="all" checked={scope === 'all'} onChange={() => setScope('all')} />
                            <span className="export-csv-option-label">All fields</span>
                            <span className="export-csv-option-desc">Export every available deal field</span>
                        </label>
                        <label className="export-csv-option">
                            <input type="radio" name="exportScope" value="selected" checked={scope === 'selected'} onChange={() => setScope('selected')} />
                            <span className="export-csv-option-label">Selected fields</span>
                            <span className="export-csv-option-desc">Only the fields chosen in Display Settings</span>
                        </label>
                    </div>
                    <div className="form-actions">
                        <Button type="button" className="btn btn-secondary" variant="secondary" id="exportCsvCancel" onClick={onClose}>Cancel</Button>
                        <Button type="button" className="btn btn-primary" variant="default" id="exportCsvOk" onClick={handleOk}>OK</Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}